import type { RestClient } from "./restClient";

export interface RoleHolder {
  user_id: string;
  role: string;
  granted_by: string | null;
}

export interface RolesResponse {
  company_id: string;
  /** All role names defined for this company (discovered dynamically). */
  roles: string[];
  holders: RoleHolder[];
  role_permissions: Record<string, string[]>;
}

export interface PermissionItem {
  key: string;
  label: string;
  description: string;
}

export interface PermissionGroup {
  area: string;
  items: PermissionItem[];
}

export interface PermissionsResponse {
  groups: PermissionGroup[];
}

/** Company-scoped role & permission admin. All endpoints require the
 *  `roles:manage` permission on the server — UI gates cosmetically. */
export class RolesApi {
  constructor(private client: RestClient) {}

  list(): Promise<RolesResponse> {
    return this.client.get("/v1/roles");
  }

  permissions(): Promise<PermissionsResponse> {
    return this.client.get("/v1/permissions");
  }

  grant(userId: string, role: string): Promise<{ ok: boolean }> {
    return this.client.post("/v1/roles/grant", { user_id: userId, role });
  }

  revoke(userId: string, role: string): Promise<{ ok: boolean }> {
    return this.client.post("/v1/roles/revoke", { user_id: userId, role });
  }

  setPermissions(
    role: string,
    permissions: string[],
  ): Promise<{ role: string; permissions: string[] }> {
    return this.client.put(
      `/v1/roles/${encodeURIComponent(role)}/permissions`,
      { permissions },
    );
  }

  delete(role: string): Promise<{ ok: boolean }> {
    return this.client.delete(`/v1/roles/${encodeURIComponent(role)}`);
  }

  rename(role: string, newName: string): Promise<{ role: string }> {
    return this.client.patch(
      `/v1/roles/${encodeURIComponent(role)}`,
      { new_name: newName },
    );
  }
}
